import { ref, watch, computed } from 'vue'
import { defineStore } from 'pinia'
import { useRouter, useRoute } from 'vue-router'
import { api } from '@/boot/axios'
import { LocalStorage } from 'quasar'
import { backends } from '@/assets/backends'
import { showSuccess } from '@/utils/ui'
import { handleError } from '@/utils/errors'

export interface AccountBusiness {
  id: number
  name: string
  slug: string
  uuid: string
  active: boolean
  allowed_api_scopes: string[]
  managed_businesses?: {
    id: number
    name: string
    uuid: string
    slug: string
    active: boolean
    allowed_api_scopes: string[]
  }[]
}

export interface Account {
  id: number
  first_name: string | null
  last_name: string | null
  email: string
  businesses: AccountBusiness[]
}

export enum LogoutReason {
  MEMBER_OF_NO_BUSINESSES = 'MEMBER_OF_NO_BUSINESSES',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
}

export function safeInternalPath(value: unknown): string | null {
  const raw = Array.isArray(value) ? value[0] : value
  if (!raw || typeof raw !== 'string') {
    return null
  }
  let url: URL
  try {
    url = new URL(raw, 'http://local')
  } catch {
    return null
  }
  if (url.origin !== 'http://local' || url.pathname === '/login') {
    return null
  }
  return url.pathname + url.search + url.hash
}

export const useAccountStore = defineStore('account', () => {
  const router = useRouter()
  const route = useRoute()

  const backend = ref(backends.find((b) => b.hostname === window.location.hostname))
  if (!backend.value) {
    backend.value = backends.find((b) => b.countryCode === navigator.language.split('-')[1])
  }

  watch(
    backend,
    () => {
      console.log('backend', backend.value)
      LocalStorage.set('backend', backend.value)
    },
    { immediate: true },
  )

  async function getCsrfToken() {
    try {
      await api.get('/../auth/oidc/csrf')
    } catch (error: any) {
      throw handleError(error, 'Failed getting CSRF token')
    }
  }

  const account = ref<Account | null>(LocalStorage.getItem('account'))
  const accountBusinesses = computed(() => {
    const businessesWithDuplicates =
      account.value?.businesses?.flatMap((b) => [b, ...(b.managed_businesses?.map((mb) => mb) || [])]) || []
    return businessesWithDuplicates.filter((b, i) => businessesWithDuplicates.findIndex((b2) => b2.id === b.id) === i)
  })

  function getAllowedBusinessIds(businessIds: number[]) {
    const accountBusinessIds = new Set(accountBusinesses.value.map((b) => b.id))
    return Array.from(accountBusinessIds.intersection(new Set(businessIds)))
  }

  watch(account, async () => {
    if (!account.value) {
      return
    }

    if (route.name === 'Login') {
      await router.replace(safeInternalPath(route.query.next) || '/')
    }

    showSuccess(`Logged in as ${account.value.email}.`, 'info')
  })

  async function getMe() {
    try {
      const res = await api.get('/account')
      account.value = res.data
      LocalStorage.set('account', account.value)

      const businessIds = account.value?.businesses.flatMap((business) => [
        business.id,
        ...(business.managed_businesses?.map((b) => b.id) || []),
      ])

      if (!businessIds || businessIds.length < 1) {
        await logout(LogoutReason.MEMBER_OF_NO_BUSINESSES)
        return
      }

      const selectedBusinesses = LocalStorage.getItem<number[]>('selectedBusinesses')

      // filter out selected businesses that are no longer in the list of businesses
      if (selectedBusinesses) {
        let filteredSelectedBusinesses: number[] = []
        filteredSelectedBusinesses = selectedBusinesses?.filter((b) => businessIds.includes(b))
        LocalStorage.set('selectedBusinesses', filteredSelectedBusinesses)
      }
    } catch (error: any) {
      console.log('ERROR', error)
      if (error.response?.status === 404) {
        await logout()
      }
    }
  }

  const isLoggingOut = ref(false)
  const returnPath = ref<string | null>(null)

  function rememberReturnPath(path: string) {
    const safe = safeInternalPath(path)
    if (safe) {
      returnPath.value = safe
    }
  }

  function pageToReturnTo(): string | null {
    if (route.name === 'Login') {
      return safeInternalPath(route.query.next) ?? returnPath.value
    }
    return returnPath.value ?? safeInternalPath(route.fullPath)
  }

  async function logout(reason?: LogoutReason) {
    if (isLoggingOut.value) return
    const returnTo = reason === LogoutReason.MEMBER_OF_NO_BUSINESSES ? null : pageToReturnTo()
    try {
      isLoggingOut.value = true
      account.value = null
      LocalStorage.remove('account')
      const params = new URLSearchParams()
      if (reason) {
        params.set('reason', reason)
      }
      if (returnTo) {
        params.set('next', returnTo)
      }
      const loginPath = `/login${params.toString() ? `?${params.toString()}` : ''}`
      const next = window.location.origin + loginPath
      await api.post(`/../auth/oidc/logout?next=${encodeURIComponent(next)}`)
    } catch (error: any) {
      console.warn(`Failed logging out: ${error.message}`)
    } finally {
      isLoggingOut.value = false
      const query: Record<string, string> = {}
      if (reason) {
        query.reason = reason
      }
      if (returnTo) {
        query.next = returnTo
      }
      await router.push({ name: 'Login', query })
    }
  }

  return {
    account,
    accountBusinesses,
    getAllowedBusinessIds,
    backend,
    getCsrfToken,
    logout,
    getMe,
    rememberReturnPath,
  }
})
